import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, forkJoin, of } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  ClientService,
  DiagnosisService,
  MedicationService,
} from '../../../../shared/services';
import {
  PatientEditable,
  TransientPatient,
} from '../../../../shared/models/order-entry/patient';
import { take, takeUntil } from 'rxjs/operators';

import { BillingInformation } from '../../../../shared/models/billing/billing-information';
import { Client } from '../../../../shared/models/order-entry/client';
import DataSource from 'devextreme/data/data_source';
import { DiagnosisViewModel } from 'src/app/shared/models/ocl/diagnosis-view-model';
import { DirtyComponent } from '../../../../../shared/utils';
import { EthnicGroups } from 'src/app/modules/shared/models/common/ethnicity';
import { PatientService } from '../../../../shared/services';
import { Races } from 'src/app/modules/shared/models/common/race';
import { RecentPatientService } from '../../../../../shared/services/ocl';
import { ResponsibleParty } from '../../../../shared/models/order-entry/reponsible-party';
import { Room } from '../../../../shared/models/order-entry/room';
import { Unit } from '../../../../shared/models/order-entry/unit';
import ValidationEngine from 'devextreme/ui/validation_engine';
import notify from 'devextreme/ui/notify';

@Component({
  selector: 'app-patient-form',
  templateUrl: './patient-form.component.html',
  styleUrls: ['./patient-form.component.scss'],
})
export class PatientFormComponent implements OnInit, OnDestroy, DirtyComponent {
  @ViewChild('diagnosesGlassControl', { static: true })
  public diagnosesGlassControl?: ElementRef;
  @ViewChild('medicationsContainer', { static: true })
  public medicationsContainer?: ElementRef;
  @ViewChild('diagnosesContainer', { static: true })
  public diagnosesContainer?: ElementRef;
  @ViewChild('medicationsGlassControl', { static: true })
  public medicationsGlassControl?: ElementRef;
  public patientForm: UntypedFormGroup = this.fb.group({
    dob: new UntypedFormControl(null),
    gender: new UntypedFormControl(null),
    client: new UntypedFormControl(null, [Validators.required]),
    unit: new UntypedFormControl(null),
    room: new UntypedFormControl(null),
    bed: new UntypedFormControl(null),
    lastName: new UntypedFormControl(null, [Validators.required]),
    firstName: new UntypedFormControl(null, [Validators.required]),
    medicalRecordId: new UntypedFormControl(null),
    middleName: new UntypedFormControl(null),
    phone: new UntypedFormControl(null),
    email: new UntypedFormControl(null),
    mobile: new UntypedFormControl(null),
    physician: new UntypedFormControl(null),
    diagnoses: new UntypedFormControl([] as DiagnosisViewModel[]),
    medications: new UntypedFormControl(null),
    responsibleParties: new UntypedFormControl([]),
    sameAsClient: new UntypedFormControl(false),
    address: new UntypedFormControl(null, [Validators.required]),
    ssn: new UntypedFormControl(null, []),
    zipCode: new UntypedFormControl(null, [Validators.required]),
    zipCodeExtra: new UntypedFormControl('', [Validators.required]),
    insuranceInformation: new UntypedFormControl([]),
    sexualOrientation: new UntypedFormControl(null),
    genderIdentity: new UntypedFormControl(null),
    race: new UntypedFormControl(''),
    ethnicity: new UntypedFormControl(null),
  });
  public ssnMaskRule = { X: /[0-9]/ };
  public phoneMaskRule = { X: /[0-9]/ };
  public today = new Date();
  public firstDate = new Date('1/1/1900');
  public diagnosticColumns = [
    {
      caption: 'Code',
      dataField: 'code',
    },
    {
      caption: 'Name',
      dataField: 'shortName',
    },
  ];
  public gender = ['MALE', 'FEMALE', 'UNKNOWN'];
  public selectRooms: BehaviorSubject<DataSource> =
    // @ts-ignore
    new BehaviorSubject<DataSource>(null);
  public patientId = -1;

  public unitsDataSource: BehaviorSubject<DataSource> =
    // @ts-ignore
    new BehaviorSubject<DataSource>(null);

  public genderIdentityDataSource: BehaviorSubject<DataSource> =
    // @ts-ignore
    new BehaviorSubject<DataSource>(null);

  public sexualOrientationDataSource: BehaviorSubject<DataSource> =
    // @ts-ignore
    new BehaviorSubject<DataSource>(null);
  public raceDataSource: BehaviorSubject<DataSource> =
    // @ts-ignore
    new BehaviorSubject<DataSource>(null);
  public ethnicGroupsDataSource: BehaviorSubject<DataSource> =
    // @ts-ignore
    new BehaviorSubject<DataSource>(null);
  private redirectToPath?: string;
  private roomUnit?: number;
  public isLoading = false;
  public loadAddressInformation = new EventEmitter();
  public notAlert = false;
  public unitRequired = false;
  // @ts-ignore
  public diagnosisDataSource = new BehaviorSubject<DataSource>(null);
  // @ts-ignore
  public medicationsDataSource = new BehaviorSubject<DataSource>(null);
  public medicationsHeight = 100;
  public diagnosesHeight = 100;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private clientService: ClientService,
    public medicationService: MedicationService,
    public diagnosisService: DiagnosisService,
    public recentPatientService: RecentPatientService,
    public service: PatientService,
    public changeDetectorRef: ChangeDetectorRef
  ) {
    this.ssnHasError = this.ssnHasError.bind(this);
    this.handleRoomChanges = this.handleRoomChanges.bind(this);
    this.handleUnitChanges = this.handleUnitChanges.bind(this);
  }

  public ngOnInit() {
    this.isLoading = true;
    this.handleRouteParametersChanges();
    this.handleClientChanges();
    this.handleUnitChanges();
    this.handleRoomChanges();
    this.checkPatientIdThenFetchIt();
    this.diagnosisDataSource.next(
      new DataSource({
        store: this.diagnosisService.customStore(this.diagnosisDataSource),
      })
    );
    this.medicationsDataSource.next(
      new DataSource({
        store: this.medicationService.customStore(this.medicationsDataSource),
      })
    );
    this.sexualOrientationDataSource.next(
      new DataSource({
        store: this.service.sexualOrientations(
          this.sexualOrientationDataSource
        ),
      })
    );
    this.genderIdentityDataSource.next(
      new DataSource({
        store: this.service.genderIdentities(this.genderIdentityDataSource),
      })
    );
    this.raceDataSource.next(
      new DataSource({
        store: Races,
      })
    );
    this.ethnicGroupsDataSource.next(
      new DataSource({
        store: EthnicGroups,
      })
    );
  }

  public tabChanged() {
    this.changeDetectorRef.detectChanges();
  }

  private handleRouteParametersChanges() {
    const dob =
      this.route.snapshot.paramMap.get('dob') !== 'null'
        ? this.route.snapshot.paramMap.get('dob')
        : null;
    const lastName =
      this.route.snapshot.paramMap.get('lastName') !== 'null'
        ? this.route.snapshot.paramMap.get('lastName')
        : '';
    const firstName =
      this.route.snapshot.paramMap.get('firstName') !== 'null'
        ? this.route.snapshot.paramMap.get('firstName')
        : '';
    const ssn =
      this.route.snapshot.paramMap.get('ssn') !== 'null'
        ? this.route.snapshot.paramMap.get('ssn')
        : null;
    const race =
      this.route.snapshot.paramMap.get('race') !== 'null'
        ? this.route.snapshot.paramMap.get('race')
        : null;
    const genderIdentity =
      this.route.snapshot.paramMap.get('genderIdentity') !== 'null'
        ? this.route.snapshot.paramMap.get('genderIdentity')
        : null;
    const ethnicity =
      this.route.snapshot.paramMap.get('ethnicity') !== 'null'
        ? this.route.snapshot.paramMap.get('ethnicity')
        : null;
    const sexualOrientation =
      this.route.snapshot.paramMap.get('sexualOrientation') !== 'null'
        ? this.route.snapshot.paramMap.get('sexualOrientation')
        : null;
    this.patientId = this.route.snapshot.paramMap.get('id')
      ? (parseFloat(this.route.snapshot.paramMap.get('id') as string) as number)
      : -1;
    this.redirectToPath = this.route.snapshot.paramMap.get('redirectToPath') as
      | string
      | undefined;

    if (dob) {
      this.patientForm.get('dob')?.setValue(dob);
    }
    if (lastName) {
      this.patientForm.get('lastName')?.setValue(lastName);
    }
    if (firstName) {
      this.patientForm.get('firstName')?.setValue(firstName);
    }
    if (ssn) {
      this.patientForm.get('ssn')?.setValue(ssn);
    }
    if (race) {
      this.patientForm.get('race')?.setValue(race);
    }
    if (genderIdentity) {
      this.patientForm.get('genderIdentity')?.setValue(genderIdentity);
    }
    if (ethnicity && Number.isInteger(ethnicity)) {
      const eth = EthnicGroups.find(
        (x) => x.value == Number.parseInt(ethnicity)
      );
      this.patientForm.get('ethnicity')?.setValue(eth);
    }
    if (sexualOrientation) {
      this.patientForm.get('sexualOrientation')?.setValue(sexualOrientation);
    }
  }

  private handleClientChanges() {
    this.patientForm
      .get('client')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((client: Client) => {
        if (!this.isLoading) {
          this.patientForm.markAsDirty();
        }
        if (!client) {
          return;
        }

        this.unitRequired = client && client.unitsCount > 0;

        if (client) {
          this.unitsDataSource.next(
            new DataSource({
              store: this.clientService.clientUnitsCustomStore(
                client.id,
                this.unitsDataSource
              ),
            })
          );
          // if (this.unitRequired) {
          this.selectRooms.next(
            new DataSource({
              store: this.clientService.clientRoomsCustomStore(
                client.id,
                this.selectRooms
              ),
              paginate: true,
              pageSize: 10,
            })
          );
          //}
        } else {
          // @ts-ignore
          this.unitsDataSource.next(null);
          // @ts-ignore
          this.selectRooms.next(null);
        }

         if (this.patientForm.get('client')?.value?.id !== client.id) {
          this.roomUnit = undefined;
          this.patientForm.get('unit')?.setValue(null);
          this.patientForm.get('room')?.setValue(null);
          this.patientForm.get('unit')?.updateValueAndValidity();
          this.patientForm.get('room')?.updateValueAndValidity();
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  public get PatientClient() {
    return this.patientForm.get('client')?.value &&
      this.patientForm.get('client')?.value.id
      ? this.patientForm.get('client')?.value.id
      : -1;
  }

  private handleRoomChanges() {
    this.patientForm
      .get('room')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((room: Room) => {
        if (!this.isLoading) {
          this.patientForm.markAsDirty();
        }

        if (
          room !== null &&
          room !== undefined &&
          !this.patientForm.get('unit')?.value
        ) {
          this.roomUnit = room.unitId;
          this.patientForm.get('unit')?.setValue(room.unitId);
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private handleUnitChanges() {
    this.patientForm
      .get('unit')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((unit: Unit | number) => {
        if (!this.isLoading) {
          this.patientForm.markAsDirty();
        }
        if (unit !== null && unit !== undefined) {
          this.selectRooms.next(
            new DataSource({
              store: this.clientService.clientRoomsByUnitCustomStore(
                typeof unit === 'number' ? unit : unit.id,
                this.selectRooms
              ),
              paginate: true,
              pageSize: 10,
            })
          );

          if (
            this.patientForm.get('room')?.value &&
            this.roomUnit !== (typeof unit === 'number' ? unit : unit.id)
          ) {
            this.patientForm.get('room')?.setValue(null);
          }
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private checkPatientIdThenFetchIt() {
    this.isLoading = true;
    if (this.patientId !== -1) {
      this.service
        .getPatient(this.patientId)
        .pipe(take(1))
        .subscribe((patient) => {
          this.patientForm.get('bed')?.setValue(patient.bed);
          this.patientForm.get('dob')?.setValue(patient.birthDate);
          this.patientForm
            .get('medicalRecordId')
            ?.setValue(patient.medicalRecordId);
          this.patientForm.get('firstName')?.setValue(patient.firstName);
          this.patientForm.get('lastName')?.setValue(patient.lastName);
          this.patientForm.get('middleName')?.setValue(patient.middleName);
          this.patientForm.get('gender')?.setValue(patient.gender);
          this.patientForm.get('physician')?.setValue(patient.primaryPhysician);
          this.patientForm.get('ssn')?.setValue(patient.socialSecurityNumber);
          this.patientForm.get('medications')?.setValue(patient.medications);
          this.patientForm.get('diagnoses')?.setValue(patient.diagnoses);
          this.patientForm
            .get('responsibleParties')
            ?.setValue(patient.responsibleParties);
          this.patientForm.get('insuranceInformation')?.setValue(
            patient.insuranceInformation
              // @ts-ignore
              .sort((a, b) => a.order - b.order)
          );
          this.patientForm.get('phone')?.setValue(patient.phone);
          this.patientForm.get('mobile')?.setValue(patient.mobile);
          this.patientForm.get('email')?.setValue(patient.email);
          this.patientForm.get('client')?.setValue(patient.client);
          this.patientForm.get('race')?.setValue(patient.race);
          this.patientForm
            .get('genderIdentity')
            ?.setValue(patient.genderIdentity);

          if (patient.ethnicity != null) {
            const eth = EthnicGroups.find((x) => x.value == patient.ethnicity);
            this.patientForm.get('ethnicity')?.setValue(eth);
          }
          this.patientForm
            .get('sexualOrientation')
            ?.setValue(patient.sexualOrientation);

          if (patient.sameAsClientAddress) {
            this.patientForm
              .get('sameAsClient')
              ?.setValue(patient.sameAsClientAddress);
          } else {
            if (patient.zipCode) {
              this.patientForm.get('zipCode')?.setValue(patient.zipCode.code);
            }
            this.patientForm
              .get('zipCodeExtra')
              ?.setValue(patient.zipCodePlus4);
            this.patientForm.get('address')?.setValue(patient.address);
          }
          this.loadAddressInformation.emit(true);

          if (patient.room) {
            this.patientForm.get('room')?.setValue(patient.room);
          }
          if (patient.unit && !patient.room?.unitId) {
            this.patientForm.get('unit')?.setValue(patient.unit);
          }

          this.changeDetectorRef.detectChanges();
          this.isLoading = !this.isLoading;
        });
    } else {
      this.isLoading = false;
    }
  }

  public canDeactivate() {
    return this.notAlert ? false : this.patientForm.dirty;
  }

  public medicationChanged(medicationsIds: number[]) {
    this.patientForm.get('medications')?.setValue(medicationsIds);
  }

  public responsiblePartiesChanged(responsibleParties: ResponsibleParty[]) {
    this.patientForm.get('responsibleParties')?.setValue(responsibleParties);
  }

  public billingInfoChanged(billingInfo: BillingInformation[]) {
    this.patientForm.get('insuranceInformation')?.setValue(billingInfo);
  }

  public diagnoseChanged(diagnosesIds: number[]) {
    this.patientForm.get('diagnoses')?.setValue(diagnosesIds);
  }

  public async submit() {
    this.notAlert = true;
    const patientForm = ValidationEngine.validateGroup('patient');
    if (patientForm.isValid) {
      this.isLoading = true;

      const patient = new PatientEditable();
      patient.address = this.patientForm.value.address;
      patient.bed = this.patientForm.value.bed;
      patient.birthDate = this.patientForm.value.dob
        ? this.patientForm.value.dob
        : null;
      patient.medicalRecordId = this.patientForm.value.medicalRecordId;
      patient.clientId = this.patientForm.value.client.id;
      patient.firstName = this.patientForm.value.firstName;
      patient.middleName = this.patientForm.value.middleName;
      patient.lastName = this.patientForm.value.lastName;
      patient.email = this.patientForm.value.email;
      patient.socialSecurityNumber = this.patientForm.value.ssn;
      patient.phone = this.patientForm.value.phone;
      patient.mobile = this.patientForm.value.mobile;
      patient.gender = this.patientForm.value.gender;
      patient.roomId = this.patientForm.value.room
        ? this.patientForm.value.room.id
        : null;
      patient.unitId = this.patientForm.value.unit
        ? this.patientForm.value.unit.id
        : null;
      patient.zipCodePlus4 = this.patientForm.value.zipCodeExtra;
      patient.sameAsClientAddress = this.patientForm.value.sameAsClient;
      patient.zipCodeName = this.patientForm.value.zipCode;
      patient.primaryPhysicianId = this.patientForm.value.physician
        ? this.patientForm.value.physician.id
        : null;

      patient.ethnicity = this.patientForm.value.ethnicity
        ? this.patientForm.value.ethnicity.value
        : null;
      patient.sexualOrientationId = this.patientForm.value.sexualOrientation
        ? this.patientForm.value.sexualOrientation.id
        : null;
      patient.genderIdentityId = this.patientForm.value.genderIdentity
        ? this.patientForm.value.genderIdentity.id
        : null;
      patient.race = this.patientForm.value.race;

      if (this.patientForm.value.insuranceInformation.length > 0) {
        for (const bi of this.patientForm.value.insuranceInformation) {
          if (bi.differentThanPatient) {
            bi.zipCodeName =
              typeof bi.zipCode === 'string' ? bi.zipCode : bi.zipCode.code;
          } else {
            bi.zipCodeName = this.patientForm.value.zipCode;
            bi.zipCodePlus4 = this.patientForm.value.zipCodeExtra;
          }
          if (bi.relationship === 'SELF') {
            bi.zipCodeName = patient.zipCodeName;
            bi.address = patient.address;
            bi.birthDate = patient.birthDate;
            bi.firstName = patient.firstName
              ? patient.firstName.toUpperCase()
              : null;
            bi.lastName = patient.lastName
              ? patient.lastName.toUpperCase()
              : null;
            bi.gender = patient.gender;
            bi.middleName = patient.middleName
              ? patient.middleName.toUpperCase()
              : null;
          }
        }
      }

      if (this.patientForm.value.responsibleParties.length > 0) {
        for (const rp of this.patientForm.value.responsibleParties) {
          rp.zipCodeName =
            typeof rp.zipCode === 'string' ? rp.zipCode : rp.zipCode.code;
          rp.address = rp.address ? rp.address.toUpperCase() : null;
        }
      }

      if (this.patientId !== -1) {
        patient.id = this.patientId;
      }
      const request =
        this.patientId === -1
          ? this.service.createPatient(patient)
          : this.service.updatePatient(this.patientId, patient);

      request.pipe(take(1)).subscribe((p) => {
        forkJoin([
          of(p),
          this.service.saveMedications(
            p.id,
            this.patientForm.value.medications
          ),
          this.service.saveDiagnoses(p.id, this.patientForm.value.diagnoses),
          this.service.saveBillingInformation(
            p.id,
            this.patientForm.value.insuranceInformation
          ),
          this.service.saveResponsibleParties(
            p.id,
            this.patientForm.value.responsibleParties
          ),
        ]).subscribe((r) => {
          notify('Patient saved successfully!', 'success');
          if (this.patientId === -1) {
            this.recentPatientService.addRecentPatient(
              r[0] as TransientPatient
            );
          } else {
            this.recentPatientService.updatePatient(r[0] as TransientPatient);
          }

          this.patientId = r[0].id;

          if (this.redirectToPath) {
            this.redirect();
          } else {
            window.history.back();
          }
        });
      });
    }
  }

  public ssnHasError(params: any) {
    if (params.value) {
      const promise = this.service
        .isSsnUnique(params.value, this.patientId)
        .toPromise();
      this.patientForm.get('ssn')?.setErrors({ uniqueSSN: true });

      promise.then((value) => {
        if (value) {
          this.patientForm.get('ssn')?.setErrors({ uniqueSSN: true });
        } else {
          this.patientForm.get('ssn')?.setErrors(null);
        }
      });

      return promise;
    }
    this.patientForm.get('ssn')?.setErrors(null);
    return new Promise((resolve) => resolve(true));
  }

  public async goBack() {
    if (this.redirectToPath) {
      if (this.patientId === -1) {
        await this.router.navigate([this.redirectToPath]);
      } else {
        await this.redirect();
      }
    } else {
      window.history.back();
    }
  }

  private async redirect() {
    const hasParams = this.redirectToPath?.indexOf(';');
    const hasPatient = this.redirectToPath?.indexOf('patientId');
    if (hasParams === -1 && hasPatient === -1 && this.patientId !== -1) {
      await this.router.navigate([
        this.redirectToPath,
        { patientId: this.patientId },
      ]);
    } else {
      await this.router.navigateByUrl(this.redirectToPath as string);
    }
  }

  /* @HostListener('window:beforeunload', ['$event'])
   public beforeUnloadHandler(event) {
       if (this.patientForm.dirty) {
           const returnValue = 'There are unsaved changes. Do you want to continue and lose these changes?';
           event.returnValue = returnValue;
           return returnValue;
       }
   }*/

  public onDateReady(e: any) {
    const element = e.element;
    if (e.component._pickerType === 'native') {
      const button = element.querySelector('.dx-texteditor-buttons-container');
      const input = element.querySelector('.dx-texteditor-input');
      this.renderer.setStyle(button, 'display', 'none');
      this.renderer.setStyle(input, 'padding-right', '5px');
    }
  }

  public convertToFormControl(absCtrl: AbstractControl | null): UntypedFormControl {
    return absCtrl as UntypedFormControl;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
